import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService, productService, reservationService, wishlistService, authService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import DashboardSidebar from '../components/DashboardSidebar';
import { isValidOptionalPhone } from '../utils/phoneValidation';
import { getImageUrl } from '../utils/imageUtils';
import ImageCropper from '../components/ImageCropper';
import UserSchedulesSection from '../components/sedes/UserSchedulesSection';

// ── Email display helpers (from original Profile.js) ─────────────────────────

const normalizeEmailRow = (email) => ({
  ...email,
  primary: email?.primary ?? email?.isPrimary ?? false,
});

const compareEmailRows = (left, right) => {
  if (left.primary !== right.primary) return left.primary ? -1 : 1;
  if (left.verified !== right.verified) return left.verified ? -1 : 1;
  if (left.provider === 'local' && right.provider !== 'local') return -1;
  if (left.provider !== 'local' && right.provider === 'local') return 1;
  const lc = left.createdAt || '', rc = right.createdAt || '';
  if (lc !== rc) return lc.localeCompare(rc);
  return left.id - right.id;
};

const buildDisplayEmails = (emailRows) => {
  const grouped = new Map();
  emailRows.forEach((row) => {
    const n = normalizeEmailRow(row);
    const key = (n.email || '').trim().toLowerCase();
    if (!key) return;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(n);
  });

  return Array.from(grouped.values())
    .map((rows) => {
      const sorted = [...rows].sort(compareEmailRows);
      const primaryRow = sorted.find((r) => r.primary) || null;
      const verified = sorted.filter((r) => r.verified);
      const unverified = sorted.filter((r) => !r.verified);
      const prefVerified = verified.find((r) => r.provider === 'local') || verified[0] || null;
      const prefUnverified = unverified.find((r) => r.provider === 'local') || unverified[0] || null;
      const display = primaryRow || prefVerified || prefUnverified || sorted[0];
      return {
        id: display.id,
        email: display.email,
        primary: Boolean(primaryRow),
        hasUnverified: unverified.length > 0,
        setPrimaryId: prefVerified?.id ?? null,
        resendVerificationId: prefUnverified?.id ?? null,
        deleteId: sorted.length === 1 ? sorted[0].id : null,
      };
    })
    .sort((a, b) => {
      if (a.primary !== b.primary) return a.primary ? -1 : 1;
      return a.email.localeCompare(b.email);
    });
};

// ── Toast component ──────────────────────────────────────────────────────────

function Toast({ message, type, onClose }) {
  if (!message) return null;
  return (
    <div className="fixed dashboard-floating-bottom left-1/2 -translate-x-1/2 z-[100] animate-fade-in">
      <div className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg text-sm font-bold ${type === 'error' ? 'bg-error text-on-error' : 'bg-primary text-on-primary'}`}>
        <span className="material-symbols-outlined text-base">{type === 'error' ? 'error' : 'check_circle'}</span>
        {message}
        <a onClick={onClose} className="ml-2 cursor-pointer opacity-70 hover:opacity-100">
          <span className="material-symbols-outlined text-base">close</span>
        </a>
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

function ProfileNew() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const theme = getThemeVars(ACTIVE_THEME);

  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone: '' });
  const [phoneError, setPhoneError] = useState('');
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const [addEmailOpen, setAddEmailOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [pwModal, setPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [showPwCurrent, setShowPwCurrent] = useState(false);
  const [showPwNew, setShowPwNew] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: 'success' }), 4000);
  }, []);

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['userProfile'],
    queryFn: async () => {
      const r = await userService.getMe();
      return r.data;
    },
    enabled: !!user,
  });

  const { data: emails = [] } = useQuery({
    queryKey: ['userEmails'],
    queryFn: async () => {
      const r = await userService.getMyEmails();
      const data = r.data || [];
      return data.map(normalizeEmailRow);
    },
    enabled: !!user,
  });

  const { data: communityCount = 0 } = useQuery({
    queryKey: ['userCommunityCount', user?.id],
    queryFn: async () => {
      const r = await userService.getUserCommunityCount(user.id);
      return r.data ?? 0;
    },
    enabled: !!user?.id,
  });

  const { data: myProducts = [], isLoading: productsLoading } = useQuery({
    queryKey: ['myProducts'],
    queryFn: async () => {
      const r = await productService.getProductsByCreator(user.id);
      return r.data || [];
    },
    enabled: !!user,
  });

  const { data: reservations = [], isLoading: reservationsLoading } = useQuery({
    queryKey: ['reservations'],
    queryFn: async () => {
      const r = await reservationService.getMyReservations();
      const list = r.data || [];
      if (!Array.isArray(list)) return [];
      // Enrich con product (nombre + imagen) para que la card no muestre "Producto #X"
      // sin imagen. La API de reservations sólo devuelve productId.
      const enriched = await Promise.all(list.map(async (res) => {
        const productId = res.productId;
        if (!productId) return res;
        try {
          const pRes = await productService.getProductById(productId);
          return { ...res, product: pRes.data };
        } catch {
          return res;
        }
      }));
      return enriched;
    },
    enabled: !!user,
  });

  const { data: wishlistItems = [], isLoading: wishlistLoading } = useQuery({
    queryKey: ['wishlist'],
    queryFn: async () => {
      const r = await wishlistService.getWishlist();
      const list = r.data || [];
      return Array.isArray(list) ? list : [];
    },
    enabled: !!user,
  });

  // Sync edit form when profile loads
  useEffect(() => {
    if (profile && !editMode) {
      setEditForm({ name: profile.name || '', phone: profile.phone || '' });
    }
  }, [profile, editMode]);

  // Deep-link: /profile?addEmail=1 abre directamente el dialog de agregar mail.
  // Lo usa /communities cuando el usuario no tiene comunidades para sugerirle
  // sumar su mail institucional (ITBA, Tarbut, etc).
  useEffect(() => {
    if (searchParams.get('addEmail') === '1') {
      setAddEmailOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('addEmail');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // ── Mutations ────────────────────────────────────────────────────────────

  const invalidateProfile = () => {
    queryClient.invalidateQueries({ queryKey: ['userProfile'] });
  };
  const invalidateEmails = () => {
    queryClient.invalidateQueries({ queryKey: ['userEmails'] });
  };

  const updateProfileMutation = useMutation({
    mutationFn: (data) => userService.updateProfile(data),
    onSuccess: () => {
      invalidateProfile();
      setEditMode(false);
      showToast(t('Perfil actualizado correctamente'));
    },
    onError: () => showToast(t('No se pudo actualizar el perfil'), 'error'),
  });

  // Cache-buster para forzar al browser a refetchear la imagen tras un
  // upload exitoso (incluso si el backend reusa el mismo imageId).
  const [pictureVersion, setPictureVersion] = useState(() => Date.now());

  const uploadPictureMutation = useMutation({
    mutationFn: (file) => userService.uploadProfilePicture(file),
    onSuccess: async () => {
      // Forzamos el refetch (no sólo invalidate) para que la respuesta del
      // GET /users/me llegue ya y el avatar tenga el nuevo profilePictureId
      // antes de mostrar el toast.
      await queryClient.refetchQueries({ queryKey: ['userProfile'], exact: true });
      // El imageId puede ser distinto, pero por las dudas (si el backend lo
      // reusa) bumpeamos un cache-buster en el `?v=` del URL.
      setPictureVersion(Date.now());
      showToast(t('Foto de perfil actualizada exitosamente'));
    },
    onError: (err) => {
      const msg = err?.response?.data?.message || t('No se pudo subir la imagen');
      showToast(msg, 'error');
    },
  });

  const addEmailMutation = useMutation({
    mutationFn: (email) => userService.addEmail(email),
    onSuccess: () => {
      invalidateEmails();
      setNewEmail('');
      setAddEmailOpen(false);
      showToast(t('Correo agregado correctamente. Revisa tu bandeja para verificarlo.'));
    },
    onError: (err) => showToast(err.response?.data?.message || t('No se pudo agregar el correo'), 'error'),
  });

  const setPrimaryMutation = useMutation({
    mutationFn: (emailId) => userService.setPrimaryEmail(emailId),
    onSuccess: () => { invalidateEmails(); showToast(t('Correo principal actualizado')); },
    onError: () => showToast(t('No se pudo actualizar el correo principal'), 'error'),
  });

  const deleteEmailMutation = useMutation({
    mutationFn: (emailId) => userService.deleteEmail(emailId),
    onSuccess: () => { invalidateEmails(); showToast(t('Correo eliminado correctamente')); },
    onError: () => showToast(t('No se pudo eliminar el correo'), 'error'),
  });

  const resendVerificationMutation = useMutation({
    mutationFn: (emailId) => userService.resendVerificationEmail(emailId),
    onSuccess: () => showToast(t('Correo de verificación enviado')),
    onError: () => showToast(t('No se pudo enviar la verificación'), 'error'),
  });

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSave = () => {
    if (!editForm.name.trim()) {
      showToast(t('Name is required'), 'error');
      return;
    }
    if (!isValidOptionalPhone(editForm.phone)) {
      setPhoneError(t('Ingresá un número de teléfono válido'));
      showToast(t('Ingresá un número de teléfono válido'), 'error');
      return;
    }
    setPhoneError('');
    updateProfileMutation.mutate({ name: editForm.name, phone: editForm.phone });
  };

  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperFile, setCropperFile] = useState(null);

  // src del avatar — preferimos `profilePictureId` (con cache-buster) y
  // caemos a `profilePictureUrl` que devuelve el backend si por alguna
  // razón el id no llegó (ej. backend desactualizado).
  const pictureSrc = profile?.profilePictureId
    ? getImageUrl(profile.profilePictureId, pictureVersion)
    : (profile?.profilePictureUrl
        ? `${profile.profilePictureUrl}${profile.profilePictureUrl.includes('?') ? '&' : '?'}v=${encodeURIComponent(pictureVersion)}`
        : null);
  const hasPicture = !!pictureSrc;

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // reset para permitir reupload del mismo archivo
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      showToast(t('Image must be under 5MB'), 'error');
      return;
    }
    // Abrir cropper Instagram-style (redondel) para que el usuario centre
    // la foto antes de subirla.
    const reader = new FileReader();
    reader.onload = (evt) => {
      setCropperImage(evt.target.result);
      setCropperFile(file);
      setCropperOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropSave = (croppedFile) => {
    // El cropper ya devuelve un File listo (con name + type correctos) y se
    // auto-cierra en handleClose. No envolvemos doble — eso podía generar
    // problemas con multipart en algunos backends.
    if (!croppedFile) return;
    uploadPictureMutation.mutate(croppedFile);
  };

  const handleAddEmail = () => {
    if (!newEmail || !newEmail.includes('@')) {
      showToast(t('Enter a valid email address'), 'error');
      return;
    }
    // Bloquear plus-aliasing (matute+algo@gmail.com → mismo inbox que matute@gmail.com).
    // El backend igual lo rechaza vía EmailValidator.requireNoPlusAlias.
    const atIdx = newEmail.indexOf('@');
    const localPart = atIdx < 0 ? newEmail : newEmail.substring(0, atIdx);
    if (localPart.includes('+')) {
      showToast(t("El email no puede contener '+' antes de '@'. Usá tu dirección base."), 'error');
      return;
    }
    addEmailMutation.mutate(newEmail);
  };

  const handleLinkGoogle = async () => {
    try {
      const r = await authService.linkGoogleAccount();
      const authUrl = r.data.authUrl;
      window.location.href = authUrl;
    } catch {
      showToast(t('No se pudo vincular la cuenta de Google'), 'error');
    }
  };

  const handleLogout = () => {
    setConfirmDialog({
      open: true,
      title: t('Cerrar sesión'),
      message: t('¿Cerrar sesión de tu cuenta en este dispositivo?'),
      onConfirm: () => {
        queryClient.clear();
        logout();
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        navigate('/login', { replace: true });
      },
    });
  };

  const handleChangePassword = async () => {
    setPwError('');
    if (!pwForm.current) { setPwError(t('Ingresá tu contraseña actual')); return; }
    if (pwForm.newPw.length < 8) { setPwError(t('La contraseña debe tener al menos 8 caracteres')); return; }
    if (pwForm.newPw !== pwForm.confirm) { setPwError(t('Las contraseñas no coinciden')); return; }
    setPwLoading(true);
    try {
      await userService.changePassword(pwForm.current, pwForm.newPw);
      setPwSuccess(true);
      setPwForm({ current: '', newPw: '', confirm: '' });
      setTimeout(() => { setPwModal(false); setPwSuccess(false); }, 2000);
    } catch (err) {
      const msg = err?.response?.data?.message;
      setPwError(msg || t('Error al cambiar la contraseña'));
    } finally {
      setPwLoading(false);
    }
  };

  const handleDeleteEmail = (email) => {
    if (!email.deleteId) return;
    if (email.primary) { showToast(t('No se puede eliminar el correo principal'), 'error'); return; }
    setConfirmDialog({
      open: true,
      title: t('Eliminar email'),
      message: t('¿Eliminar {{email}} de tu cuenta?', { email: email.email }),
      onConfirm: () => {
        deleteEmailMutation.mutate(email.deleteId);
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
      },
    });
  };

  // ── Derived data ─────────────────────────────────────────────────────────

  const displayEmails = buildDisplayEmails(emails);
  const primaryEmail = displayEmails.find((e) => e.primary) || displayEmails[0];
  const userName = profile?.name || user?.name || 'User';
  const userInitial = userName.charAt(0).toUpperCase();

  const productStats = {
    total: myProducts.length,
    available: myProducts.filter((p) => p.status === 'AVAILABLE').length,
    sold: myProducts.filter((p) => p.status === 'SOLD').length,
    views: myProducts.reduce((s, p) => s + (p.viewCount || 0), 0),
  };

  const reservationStats = {
    total: reservations.length,
    active: reservations.filter((r) => r.status === 'ACTIVE').length,
    completed: reservations.filter((r) => r.status === 'ACCEPTED').length,
  };

  const STATUS_STYLE = {
    AVAILABLE: 'bg-primary/10 text-primary',
    RESERVED:  'bg-secondary/10 text-secondary',
    SOLD:      'bg-error-dim/10 text-error-dim',
  };

  const STATUS_LABEL = {
    AVAILABLE: t('Disponible'),
    RESERVED: t('Reservado'),
    SOLD: t('Vendido'),
  };

  const RES_STATUS = {
    ACTIVE:    { label: t('Pendiente'),  icon: 'pending',      color: 'text-primary' },
    ACCEPTED:  { label: t('Completada'), icon: 'check_circle', color: 'text-on-surface-variant/40' },
    REJECTED:  { label: t('Rechazada'),  icon: 'cancel',       color: 'text-error-dim' },
    WITHDRAWN: { label: t('Cancelada'),  icon: 'cancel',       color: 'text-error-dim' },
    EXPIRED:   { label: t('Expirada'),   icon: 'schedule',     color: 'text-on-surface-variant/40' },
  };

  // ── Loading state ────────────────────────────────────────────────────────

  const isSubmitting = updateProfileMutation.isPending || uploadPictureMutation.isPending
    || addEmailMutation.isPending || setPrimaryMutation.isPending
    || deleteEmailMutation.isPending || resendVerificationMutation.isPending;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <DashboardSidebar activePath="/profile" />
      <main className="flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
        <div className="px-6 tablet-sm:px-12 pt-8 pb-20 max-w-5xl mx-auto">

          {profileLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* ── Profile Header — MOBILE ── */}
              <div className="tablet-sm:hidden mb-6">
                {!editMode ? (
                  <>
                    <div className="flex items-start gap-3 mb-4">
                      {/* Avatar */}
                      <div className="relative group shrink-0">
                        {hasPicture ? (
                          <img
                            src={pictureSrc}
                            alt=""
                            className="w-20 h-20 rounded-full object-cover shadow-md"
                          />
                        ) : (
                          <div className="w-20 h-20 rounded-full bg-primary-container flex items-center justify-center text-2xl font-black text-on-primary-container shadow-md">
                            {userInitial}
                          </div>
                        )}
                        <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full cursor-pointer">
                          <span className="material-symbols-outlined text-white text-xl">photo_camera</span>
                          <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                        </label>
                      </div>
                      {/* Acciones con icono — compactas a la derecha del avatar */}
                      <div className="flex items-center gap-2 flex-1 justify-end self-center">
                        <button
                          onClick={() => setEditMode(true)}
                          className="w-11 h-11 rounded-xl bg-surface-container-highest flex items-center justify-center text-on-surface hover:bg-surface-variant transition-colors"
                          title={t('Editar perfil')}
                          aria-label={t('Editar perfil')}
                        >
                          <span className="material-symbols-outlined text-[20px]">edit</span>
                        </button>
                        <button
                          onClick={() => { setPwModal(true); setPwError(''); setPwForm({ current: '', newPw: '', confirm: '' }); }}
                          className="w-11 h-11 rounded-xl border border-outline-variant flex items-center justify-center text-on-surface hover:bg-surface-container transition-colors"
                          title={t('Cambiar contraseña')}
                          aria-label={t('Cambiar contraseña')}
                        >
                          <span className="material-symbols-outlined text-[20px]">lock</span>
                        </button>
                        <button
                          onClick={handleLogout}
                          className="w-11 h-11 rounded-xl bg-error/10 flex items-center justify-center text-error-dim hover:bg-error/15 transition-colors"
                          title={t('Cerrar sesión')}
                          aria-label={t('Cerrar sesión')}
                        >
                          <span className="material-symbols-outlined text-[20px]">logout</span>
                        </button>
                      </div>
                    </div>
                    <h1 className="text-2xl font-black text-on-surface tracking-tight">{userName}</h1>
                    <p className="text-sm text-on-surface-variant mt-1">{primaryEmail?.email || ''}</p>
                    {profile?.phone && (
                      <p className="text-sm text-on-surface-variant mt-0.5 flex items-center gap-1">
                        <span className="material-symbols-outlined text-sm">phone</span>
                        {profile.phone}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="flex items-start gap-3">
                    <div className="relative group shrink-0">
                      {hasPicture ? (
                        <img
                          src={pictureSrc}
                          alt=""
                          className="w-20 h-20 rounded-full object-cover shadow-md"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-primary-container flex items-center justify-center text-2xl font-black text-on-primary-container shadow-md">
                          {userInitial}
                        </div>
                      )}
                      <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full cursor-pointer">
                        <span className="material-symbols-outlined text-white text-xl">photo_camera</span>
                        <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                      </label>
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                        className="w-full bg-surface-container border border-outline-variant/30 rounded-xl px-3 py-2 text-on-surface text-sm font-bold focus:outline-none focus:ring-2 focus:ring-primary/40"
                        placeholder={t('Name')}
                      />
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-on-surface-variant text-base">phone</span>
                        <input
                          type="text"
                          value={editForm.phone}
                          onChange={(e) => {
                            setEditForm((p) => ({ ...p, phone: e.target.value }));
                            if (phoneError) setPhoneError(isValidOptionalPhone(e.target.value) ? '' : t('Enter a valid phone number'));
                          }}
                          className={`flex-1 bg-surface-container border rounded-xl px-3 py-2 text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 ${phoneError ? 'border-error' : 'border-outline-variant/30'}`}
                          placeholder={t('Phone (optional)')}
                        />
                      </div>
                      {phoneError && <p className="text-xs text-error">{phoneError}</p>}
                      <div className="flex gap-2">
                        <a
                          onClick={handleSave}
                          className={`flex-1 text-center px-3 py-2 bg-primary text-on-primary rounded-xl text-sm font-bold cursor-pointer ${isSubmitting ? 'opacity-50 pointer-events-none' : 'hover:opacity-90'}`}
                        >
                          {t('Guardar')}
                        </a>
                        <a
                          onClick={() => { setEditMode(false); setPhoneError(''); setEditForm({ name: profile?.name || '', phone: profile?.phone || '' }); }}
                          className="flex-1 text-center px-3 py-2 bg-surface-container-highest text-on-surface-variant rounded-xl text-sm font-bold cursor-pointer hover:bg-surface-variant transition-colors"
                        >
                          {t('Cancelar')}
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Profile Header — DESKTOP ── */}
              <div className="hidden tablet-sm:flex flex-row items-start gap-6 mb-10">
                {/* Avatar */}
                <div className="relative group">
                  {hasPicture ? (
                    <img
                      src={pictureSrc}
                      alt=""
                      className="w-24 h-24 rounded-full object-cover shadow-md"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-primary-container flex items-center justify-center text-3xl font-black text-on-primary-container shadow-md">
                      {userInitial}
                    </div>
                  )}
                  <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                    <span className="material-symbols-outlined text-white text-2xl">photo_camera</span>
                    <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                  </label>
                </div>

                {/* Name & email */}
                <div className="flex-1">
                  {editMode ? (
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                        className="w-full bg-surface-container border border-outline-variant/30 rounded-xl px-4 py-2.5 text-on-surface text-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary/40"
                        placeholder={t('Name')}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-on-surface-variant text-base">phone</span>
                          <input
                            type="text"
                            value={editForm.phone}
                            onChange={(e) => {
                              setEditForm((p) => ({ ...p, phone: e.target.value }));
                              if (phoneError) setPhoneError(isValidOptionalPhone(e.target.value) ? '' : t('Enter a valid phone number'));
                            }}
                            className={`flex-1 bg-surface-container border rounded-xl px-4 py-2 text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 ${phoneError ? 'border-error' : 'border-outline-variant/30'}`}
                            placeholder={t('Phone (optional)')}
                          />
                        </div>
                        {phoneError && <p className="text-xs text-error mt-1 ml-7">{phoneError}</p>}
                      </div>
                      <div className="flex gap-2">
                        <a
                          onClick={handleSave}
                          className={`px-5 py-2 bg-primary text-on-primary rounded-xl text-sm font-bold cursor-pointer transition-opacity ${isSubmitting ? 'opacity-50 pointer-events-none' : 'hover:opacity-90'}`}
                        >
                          {t('Guardar')}
                        </a>
                        <a
                          onClick={() => { setEditMode(false); setPhoneError(''); setEditForm({ name: profile?.name || '', phone: profile?.phone || '' }); }}
                          className="px-5 py-2 bg-surface-container-highest text-on-surface-variant rounded-xl text-sm font-bold cursor-pointer hover:bg-surface-variant transition-colors"
                        >
                          {t('Cancelar')}
                        </a>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h1 className="text-3xl font-black text-on-surface tracking-tight">{userName}</h1>
                      <p className="text-sm text-on-surface-variant mt-1">{primaryEmail?.email || ''}</p>
                      {profile?.phone && (
                        <p className="text-sm text-on-surface-variant mt-0.5 flex items-center gap-1">
                          <span className="material-symbols-outlined text-sm">phone</span>
                          {profile.phone}
                        </p>
                      )}
                    </>
                  )}
                </div>

                {!editMode ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <a
                      onClick={() => setEditMode(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-surface-container-highest text-on-surface rounded-xl text-sm font-bold cursor-pointer hover:bg-surface-variant transition-colors"
                    >
                      <span className="material-symbols-outlined text-base">edit</span>
                      {t('Editar perfil')}
                    </a>
                    <button
                      onClick={() => { setPwModal(true); setPwError(''); setPwForm({ current: '', newPw: '', confirm: '' }); }}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-outline-variant text-sm font-medium text-on-surface hover:bg-surface-container transition-colors"
                    >
                      <span className="material-symbols-outlined text-base">lock</span>
                      {t('Cambiar contraseña')}
                    </button>
                    <a
                      onClick={handleLogout}
                      className="flex items-center gap-2 px-4 py-2 bg-error/10 text-error-dim rounded-xl text-sm font-bold cursor-pointer hover:bg-error/15 transition-colors"
                    >
                      <span className="material-symbols-outlined text-base">logout</span>
                      {t('Cerrar sesión')}
                    </a>
                  </div>
                ) : null}
              </div>

              {/* ── Stats Row — MOBILE (4 chiquitas en una fila) ── */}
              <div className="grid grid-cols-4 gap-1.5 mb-6 tablet-sm:hidden">
                {[
                  { label: t('Productos'), value: productStats.total, icon: 'inventory_2', color: 'text-primary' },
                  { label: t('Disponibles'), value: productStats.available, icon: 'storefront', color: 'text-secondary' },
                  { label: t('Vendidos'), value: productStats.sold, icon: 'sell', color: 'text-error-dim' },
                  { label: t('Comunidades'), value: communityCount, icon: 'group_work', color: 'text-tertiary' },
                ].map(({ label, value, icon, color }) => (
                  <div key={label} className="bg-surface-container rounded-lg p-2 flex flex-col items-center gap-0.5">
                    <span className={`material-symbols-outlined text-base ${color}`}>{icon}</span>
                    <span className={`text-lg font-black leading-none ${color}`}>{value}</span>
                    <span className="text-[9px] uppercase tracking-tight font-bold text-on-surface-variant text-center leading-tight px-0.5">{label}</span>
                  </div>
                ))}
              </div>

              {/* ── Stats Row — DESKTOP ── */}
              <div className="hidden tablet-sm:grid grid-cols-4 gap-4 mb-10">
                {[
                  { label: t('Productos totales'), value: productStats.total, icon: 'inventory_2', color: 'text-primary' },
                  { label: t('Disponibles'), value: productStats.available, icon: 'storefront', color: 'text-secondary' },
                  { label: t('Vendidos'), value: productStats.sold, icon: 'sell', color: 'text-error-dim' },
                  { label: t('Comunidades'), value: communityCount, icon: 'group_work', color: 'text-tertiary' },
                ].map(({ label, value, icon, color }) => (
                  <div key={label} className="bg-surface-container p-5 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`material-symbols-outlined text-base ${color}`}>{icon}</span>
                      <span className="text-[10px] uppercase tracking-widest font-bold text-on-surface-variant">{label}</span>
                    </div>
                    <span className={`text-2xl font-black ${color}`}>{value}</span>
                  </div>
                ))}
              </div>

              {/* ── Cards Grid ── */}
              <div className="grid grid-cols-1 tablet-lg:grid-cols-2 gap-8">

                {/* ═══════ Emails Card ═══════ */}
                <div className="bg-surface border border-outline rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant/10">
                    <h3 className="text-lg font-bold text-on-surface flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary">mail</span>
                      {t('Mails')}
                    </h3>
                    <div className="flex gap-2">
                      <a
                        onClick={() => setAddEmailOpen(true)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-primary text-on-primary rounded-lg text-xs font-bold cursor-pointer hover:opacity-90 transition-opacity"
                      >
                        <span className="material-symbols-outlined text-sm">add</span>
                        {t('Agregar')}
                      </a>
                      <a
                        onClick={handleLinkGoogle}
                        className="flex items-center gap-1 px-3 py-1.5 bg-surface-container-highest text-on-surface-variant rounded-lg text-xs font-bold cursor-pointer hover:bg-surface-variant transition-colors"
                      >
                        <span className="material-symbols-outlined text-sm">link</span>
                        Google
                      </a>
                    </div>
                  </div>

                  {displayEmails.length === 0 ? (
                    <div className="py-12 flex flex-col items-center text-center px-6">
                      <span className="material-symbols-outlined text-4xl text-outline-variant mb-3">alternate_email</span>
                      <p className="text-sm text-on-surface-variant">{t('No tenés direcciones de correo. Agregá una para empezar.')}</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-outline-variant/10">
                      {displayEmails.map((email) => (
                        <div key={email.id} className="flex items-center gap-3 px-6 py-3.5 hover:bg-surface-container-high/30 transition-colors group">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-on-surface truncate">{email.email}</span>
                              {email.primary && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold uppercase shrink-0">
                                  {t('Principal')}
                                </span>
                              )}
                              {email.hasUnverified && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-secondary/10 text-secondary text-[10px] font-bold uppercase shrink-0">
                                  {t('Sin verificar')}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-100 tablet-sm:opacity-0 tablet-sm:group-hover:opacity-100 transition-opacity shrink-0">
                            {!email.primary && email.setPrimaryId && (
                              <a
                                onClick={() => setPrimaryMutation.mutate(email.setPrimaryId)}
                                className="p-1.5 rounded-lg hover:bg-surface-container-highest cursor-pointer transition-colors"
                                title={t('Marcar como principal')}
                              >
                                <span className="material-symbols-outlined text-base text-on-surface-variant">star</span>
                              </a>
                            )}
                            {email.resendVerificationId && (
                              <a
                                onClick={() => resendVerificationMutation.mutate(email.resendVerificationId)}
                                className="p-1.5 rounded-lg hover:bg-surface-container-highest cursor-pointer transition-colors"
                                title={t('Reenviar verificación')}
                              >
                                <span className="material-symbols-outlined text-base text-on-surface-variant">refresh</span>
                              </a>
                            )}
                            {!email.primary && email.deleteId && (
                              <a
                                onClick={() => handleDeleteEmail(email)}
                                className="p-1.5 rounded-lg hover:bg-error/10 cursor-pointer transition-colors"
                                title={t('Eliminar correo')}
                              >
                                <span className="material-symbols-outlined text-base text-error-dim">delete</span>
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ═══════ Products Card ═══════ */}
                <div className="bg-surface border border-outline rounded-xl overflow-hidden group">
                  <a
                    onClick={() => navigate('/my-products')}
                    className="flex items-center justify-between px-6 py-4 border-b border-outline-variant/10 cursor-pointer hover:bg-surface-container-high/30 transition-colors"
                  >
                    <h3 className="text-lg font-bold text-on-surface flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary">inventory_2</span>
                      {t('Mis Productos')}
                    </h3>
                    <div className="flex items-center gap-2 text-xs font-bold text-on-surface-variant">
                      <span>{myProducts.length} {t('productos')}</span>
                      <span className="material-symbols-outlined text-base group-hover:translate-x-0.5 transition-transform">chevron_right</span>
                    </div>
                  </a>

                  {productsLoading ? (
                    <div className="divide-y divide-outline-variant/10">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-3 px-6 py-3 animate-pulse">
                          <div className="w-10 h-10 rounded-lg bg-surface-container-highest" />
                          <div className="flex-1 space-y-1.5">
                            <div className="h-3.5 bg-surface-container-highest rounded w-2/3" />
                            <div className="h-2.5 bg-surface-container-highest rounded w-1/3" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : myProducts.length === 0 ? (
                    <div className="py-12 flex flex-col items-center text-center px-6">
                      <span className="material-symbols-outlined text-4xl text-outline-variant mb-3">storefront</span>
                      <p className="text-sm font-bold text-on-surface mb-1">{t('Sin productos aún')}</p>
                      <p className="text-xs text-on-surface-variant">{t('Publica tu primer producto para empezar a vender.')}</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-outline-variant/10">
                      {myProducts.slice(0, 5).map((product) => {
                        const imageUrl = getImageUrl(product.imageId);
                        return (
                          <a
                            key={product.id}
                            onClick={() => navigate(`/products/${product.id}`)}
                            className="flex items-center gap-3 px-6 py-3 cursor-pointer hover:bg-surface-container-high/30 transition-colors"
                          >
                            {imageUrl ? (
                              <img className="w-10 h-10 rounded-lg object-cover bg-surface-container-highest shrink-0" src={imageUrl} alt="" />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-surface-container-highest flex items-center justify-center shrink-0">
                                <span className="material-symbols-outlined text-sm text-on-surface-variant">shopping_bag</span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-on-surface truncate">{product.name}</p>
                              {product.price != null && (
                                <span className="text-xs font-semibold text-primary mt-0.5 inline-block">${product.price}</span>
                              )}
                            </div>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase shrink-0 ${STATUS_STYLE[product.status] || ''}`}>
                              {STATUS_LABEL[product.status] || product.status}
                            </span>
                          </a>
                        );
                      })}
                      {myProducts.length > 5 && (
                        <a
                          onClick={() => navigate('/my-products')}
                          className="block px-6 py-3 text-center cursor-pointer hover:bg-surface-container-high/30 transition-colors"
                        >
                          <span className="text-xs font-bold text-primary">
                            +{myProducts.length - 5} {t('productos más')}
                          </span>
                        </a>
                      )}
                    </div>
                  )}
                </div>

                {/* ═══════ Trades Card ═══════ */}
                <div
                  className="bg-surface border border-outline rounded-xl overflow-hidden cursor-pointer hover:shadow-md transition-shadow group"
                  onClick={() => navigate('/reservations')}
                >
                  <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant/10">
                    <h3 className="text-lg font-bold text-on-surface flex items-center gap-2">
                      <span className="material-symbols-outlined text-primary">swap_horiz</span>
                      <span className="tablet-sm:hidden">{t('Compraventas')}</span>
                      <span className="hidden tablet-sm:inline">{t('Mis compraventas')}</span>
                    </h3>
                    <div className="flex items-center gap-2 text-xs font-bold text-on-surface-variant">
                      <span>
                        {reservations.length}
                        <span className="hidden tablet-sm:inline"> {t('compraventas')}</span>
                      </span>
                      <span className="material-symbols-outlined text-base group-hover:translate-x-0.5 transition-transform">chevron_right</span>
                    </div>
                  </div>

                  {reservationsLoading ? (
                    <div className="divide-y divide-outline-variant/10">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-3 px-6 py-3 animate-pulse">
                          <div className="w-8 h-8 rounded-lg bg-surface-container-highest" />
                          <div className="flex-1 space-y-1.5">
                            <div className="h-3.5 bg-surface-container-highest rounded w-2/3" />
                            <div className="h-2.5 bg-surface-container-highest rounded w-1/3" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : reservations.length === 0 ? (
                    <div className="py-12 flex flex-col items-center text-center px-6">
                      <span className="material-symbols-outlined text-4xl text-outline-variant mb-3">swap_horiz</span>
                      <p className="text-sm font-bold text-on-surface mb-1">{t('Sin compraventas aún')}</p>
                      <p className="text-xs text-on-surface-variant">{t('Reserva un producto para empezar.')}</p>
                    </div>
                  ) : (
                    <>
                      {/* Mini stats */}
                      <div className="flex items-center gap-4 px-6 py-3 border-b border-outline-variant/10">
                        <div className="flex items-center gap-1.5">
                          <span className="material-symbols-outlined text-sm text-primary">pending</span>
                          <span className="text-xs font-bold text-primary">{reservationStats.active} {t('pendientes')}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="material-symbols-outlined text-sm text-secondary" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                          <span className="text-xs font-bold text-secondary">{reservationStats.completed} {t('completadas')}</span>
                        </div>
                      </div>
                      <div className="divide-y divide-outline-variant/10">
                        {reservations.slice(0, 4).map((res) => {
                          const status = RES_STATUS[res.status] || RES_STATUS.ACTIVE;
                          const productImage = getImageUrl(res.product?.imageId);
                          const productName = res.product?.name || res.productName || t('Producto #{{id}}', { id: res.productId });
                          return (
                            <div key={res.id} className="flex items-center gap-3 px-6 py-3 hover:bg-surface-container-high/30 transition-colors">
                              <div className="w-10 h-10 rounded-lg bg-surface-container-highest overflow-hidden flex items-center justify-center shrink-0">
                                {productImage ? (
                                  <img src={productImage} alt={productName} className="w-full h-full object-cover" />
                                ) : (
                                  <span className={`material-symbols-outlined text-sm ${status.color}`}>{status.icon}</span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-on-surface truncate">{productName}</p>
                                <span className={`text-[10px] font-bold uppercase tracking-tight ${status.color}`}>{status.label}</span>
                              </div>
                            </div>
                          );
                        })}
                        {reservations.length > 4 && (
                          <div className="px-6 py-3 flex items-center justify-center">
                            <span className="text-xs font-bold text-primary">
                              +{reservations.length - 4} {t('intercambios más')}
                            </span>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* ═══════ Mis horarios ═══════
                    Reemplaza la card de "Saved Items" en este slot — los
                    favoritos ya se acceden desde la sidebar (/wishlist),
                    los horarios son un setting que vive mejor en el perfil. */}
                {user?.id && (
                  <UserSchedulesSection
                    userId={user.id}
                    userName={user.name}
                    isOwn
                  />
                )}
              </div>
            </>
          )}

      {/* ── Add Email Dialog ── */}
      {addEmailOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50" onClick={() => setAddEmailOpen(false)}>
          <div className="bg-surface border border-outline w-full max-w-md mx-4 rounded-xl shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-outline-variant/10">
              <h3 className="text-lg font-bold text-on-surface">{t('Add Email Address')}</h3>
            </div>
            <div className="px-6 py-5 space-y-4">
              {/* Google OAuth como opción preferida: lo ponemos arriba con
                  fondo primary y tamaño grande para que destaque sobre el
                  input manual. El path por Google es más rápido (sin esperar
                  correo de verificación) y queremos empujar al usuario hacia
                  ahí. */}
              <button
                onClick={handleLinkGoogle}
                className="w-full flex items-center justify-center gap-3 py-3.5 rounded-xl bg-primary text-on-primary font-bold text-base hover:opacity-90 transition-opacity shadow-md shadow-primary/20"
              >
                <span className="bg-white rounded-full p-1 flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                </span>
                {t('Vincular cuenta de Google')}
              </button>

              <div className="flex items-center gap-3 text-xs text-on-surface-variant pt-1">
                <div className="flex-1 h-px bg-outline-variant/30" />
                <span>{t('O')}</span>
                <div className="flex-1 h-px bg-outline-variant/30" />
              </div>

              <input
                type="email"
                autoFocus
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
                className="w-full bg-surface-container-low border border-outline-variant/30 rounded-xl px-4 py-2.5 text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                placeholder="email@example.com"
              />
              <div className="flex items-start gap-2 p-3 rounded-xl bg-primary/5">
                <span className="material-symbols-outlined text-primary text-base mt-0.5">info</span>
                <p className="text-xs text-on-surface-variant">{t('Te enviaremos un correo de verificación a esa dirección.')}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-outline-variant/10">
              <a
                onClick={() => { setAddEmailOpen(false); setNewEmail(''); }}
                className="px-4 py-2 text-sm font-bold text-on-surface-variant cursor-pointer hover:bg-surface-container-highest rounded-lg transition-colors"
              >
                {t('Cancelar')}
              </a>
              <a
                onClick={handleAddEmail}
                className={`px-4 py-2 bg-primary text-on-primary text-sm font-bold rounded-lg cursor-pointer transition-opacity ${addEmailMutation.isPending ? 'opacity-50 pointer-events-none' : 'hover:opacity-90'}`}
              >
                {addEmailMutation.isPending ? t('Adding...') : t('Add Email')}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm Dialog ── */}
      {confirmDialog.open && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50" onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
          <div className="bg-surface border border-outline w-full max-w-sm mx-4 rounded-xl shadow-lg" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 pt-6 pb-2">
              <h3 className="text-lg font-bold text-on-surface">{confirmDialog.title}</h3>
            </div>
            <div className="px-6 py-3">
              <p className="text-sm text-on-surface-variant">{confirmDialog.message}</p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-5">
              <a
                onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
                className="px-5 py-2.5 text-sm font-bold text-on-surface-variant cursor-pointer hover:bg-surface-container rounded-xl transition-colors"
              >
                {t('Cancelar')}
              </a>
              <a
                onClick={confirmDialog.onConfirm}
                className="px-5 py-2.5 bg-error-container text-on-error-container text-sm font-bold rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
              >
                {t('Confirmar')}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ── Password Change Modal ── */}
      {pwModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-bold text-on-surface mb-4">{t('Cambiar contraseña')}</h2>
            {pwSuccess ? (
              <div className="flex items-center gap-2 text-primary py-4">
                <span className="material-symbols-outlined">check_circle</span>
                <span>{t('Contraseña actualizada correctamente')}</span>
              </div>
            ) : (
              <div className="space-y-3">
                {pwError && <p className="text-sm text-error bg-error/10 rounded-xl px-3 py-2">{pwError}</p>}
                <div className="relative">
                  <input type={showPwCurrent ? 'text' : 'password'} value={pwForm.current}
                    onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))}
                    placeholder={t('Contraseña actual')}
                    className="w-full bg-surface-container border border-outline-variant/30 rounded-xl px-4 py-2.5 pr-10 text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
                  <button type="button" onClick={() => setShowPwCurrent(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant">
                    <span className="material-symbols-outlined text-xl">{showPwCurrent ? 'visibility_off' : 'visibility'}</span>
                  </button>
                </div>
                <div className="relative">
                  <input type={showPwNew ? 'text' : 'password'} value={pwForm.newPw}
                    onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))}
                    placeholder={t('Nueva contraseña (mín. 8 caracteres)')}
                    className="w-full bg-surface-container border border-outline-variant/30 rounded-xl px-4 py-2.5 pr-10 text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
                  <button type="button" onClick={() => setShowPwNew(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant">
                    <span className="material-symbols-outlined text-xl">{showPwNew ? 'visibility_off' : 'visibility'}</span>
                  </button>
                </div>
                <input type="password" value={pwForm.confirm}
                  onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
                  placeholder={t('Confirmar nueva contraseña')}
                  className="w-full bg-surface-container border border-outline-variant/30 rounded-xl px-4 py-2.5 text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
                <div className="flex gap-2 pt-1">
                  <button onClick={() => { setPwModal(false); setPwError(''); }}
                    className="flex-1 py-2.5 rounded-xl border border-outline-variant text-sm font-medium text-on-surface hover:bg-surface-container transition-colors">
                    {t('Cancelar')}
                  </button>
                  <button onClick={handleChangePassword} disabled={pwLoading}
                    className="flex-1 py-2.5 rounded-xl bg-primary text-on-primary text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors">
                    {pwLoading ? t('Guardando...') : t('Guardar')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'success' })} />

      {/* ── Cropper Instagram-style (redondel) para foto de perfil ── */}
      <ImageCropper
        open={cropperOpen}
        image={cropperImage}
        originalFile={cropperFile}
        cropShape="round"
        onClose={() => { setCropperOpen(false); setCropperImage(null); setCropperFile(null); }}
        onSave={handleCropSave}
      />
        </div>
      </main>
    </div>
  );
}

export default ProfileNew;
